import { z } from 'zod';
import { FOUNDER_COMPLETABLE_STEPS, ONBOARDING_DERIVED_STEPS, ONBOARDING_OVERRIDE_REASON_MIN, ONBOARDING_RESET_CONFIRM, ONBOARDING_STEPS, isFounderCompletableStep, } from '../types/onboarding.js';
/**
 * `POST /v1/onboarding/steps/:n/complete` path parameter.
 *
 * The allowlist lives in the schema, not just the route, so the 403 for a
 * webhook-owned step (1/3/8), the intake-owned step (2) or an application-derived
 * step (5/11) is impossible to forget when a second caller appears. Steps
 * outside 1–11 fail the same way.
 */
export const FounderCompletableStepSchema = z
    .coerce.number()
    .int()
    .refine(isFounderCompletableStep, {
    message: `Step is not founder-completable (allowed: ${FOUNDER_COMPLETABLE_STEPS.join(', ')})`,
});
/**
 * Optional body. `metadata` is merged into the step row — the tour uses it to
 * record whether the founder finished or skipped, which Onboarding 13 reports on.
 */
export const CompleteOnboardingStepBodySchema = z
    .object({
    metadata: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
/**
 * One intake answer, saved by the browser — `POST /v1/onboarding/intake/answers`.
 *
 * `value` is deliberately unknown: the shape differs per question (an array of
 * rows for the services grid, an object for always/never words, a list of file
 * references for brand assets), and the question registry is what validates it.
 * Restating those shapes here would be a second, drifting copy of
 * `validateIntakeAnswer`.
 */
export const SaveIntakeAnswerBodySchema = z
    .object({
    question_id: z.string().min(1),
    value: z.unknown(),
})
    .strict();
/**
 * Admin override bodies — ClickUp 86d3ukp2h (Onboarding 12).
 *
 * Two rules run through all of them.
 *
 * `reason` is REQUIRED, not optional-with-a-default. Every override lands in
 * `audit_log` via the step table's audit trigger, and an override whose reason
 * reads "admin_override" answers nothing six months later when someone asks why
 * this founder's dashboard opened without an agreement. Requiring it in the
 * schema means the API cannot grow a call site that forgets it.
 *
 * An admin may override any step EXCEPT the two the workflow owns (5 and 11).
 * Those are derived — step 5 IS the unlock, and forcing it directly would open
 * the dashboard while steps 1–4 sat open behind it. The way to unlock a founder
 * early is to complete the steps that gate it; the cascade then fires 5 itself,
 * with its invariants intact.
 */
const OverrideReasonSchema = z
    .string()
    .trim()
    .min(ONBOARDING_OVERRIDE_REASON_MIN, `Give a reason (at least ${ONBOARDING_OVERRIDE_REASON_MIN} characters) — it is recorded in the audit log`)
    .max(500);
const DERIVED = new Set(ONBOARDING_DERIVED_STEPS);
export const AdminOverridableStepSchema = z
    .number()
    .int()
    .refine((step) => ONBOARDING_STEPS.includes(step), {
    message: `Step must be one of: ${ONBOARDING_STEPS.join(', ')}`,
})
    .refine((step) => !DERIVED.has(step), {
    message: `Steps ${ONBOARDING_DERIVED_STEPS.join(' and ')} are completed by the workflow, not by an admin`,
});
/** `complete-step` and `skip-step` take the same body; only the metadata differs. */
export const AdminOnboardingStepOverrideBodySchema = z
    .object({
    step: AdminOverridableStepSchema,
    reason: OverrideReasonSchema,
})
    .strict();
/**
 * `agreement-signed` takes only a reason: the step is not a parameter because
 * this endpoint asserts one specific fact, and a caller naming a step would be
 * asking it to record a signature against something that is not the agreement.
 */
export const AdminOnboardingAgreementSignedBodySchema = z
    .object({
    reason: OverrideReasonSchema,
})
    .strict();
export const AdminOnboardingResetBodySchema = z
    .object({
    reason: OverrideReasonSchema,
    confirm: z.literal(ONBOARDING_RESET_CONFIRM, {
        errorMap: () => ({ message: `confirm must be "${ONBOARDING_RESET_CONFIRM}"` }),
    }),
})
    .strict();
/**
 * `step` is optional: the common case is "this founder is stuck, sort it out",
 * and the API can see which webhook-owned step they are stuck on. Naming one is
 * for the case where a founder is stuck on two at once.
 */
export const AdminOnboardingUnblockBodySchema = z
    .object({
    step: AdminOverridableStepSchema.optional(),
    reason: OverrideReasonSchema,
})
    .strict();
//# sourceMappingURL=onboarding.js.map